import { useEffect, useRef, useState } from 'react';

/**
 * Hook to manage map drag-state detection and map-click forwarding.
 *
 * Owns every map-gesture listener so that future additions (e.g. pinch-to-zoom,
 * configurable debounce) only need to touch this file.
 *
 * @param {google.maps.Map | null} map - The Google Maps instance
 * @param {Function} [onMapClick] - Optional callback invoked on map background click
 * @returns {boolean} isDragging - Whether the map is currently being dragged
 */
const useMapDragState = (map, onMapClick) => {
	const dragTimeoutRef = useRef(null);
	const [isDragging, setIsDragging] = useState(false);

	useEffect(() => {
		if (!map || !window.google) {
			return undefined;
		}

		const dragStartListener = map.addListener('dragstart', () => {
			setIsDragging(true);
		});

		const dragEndListener = map.addListener('dragend', () => {
			// Clear any existing timeout
			if (dragTimeoutRef.current) {
				clearTimeout(dragTimeoutRef.current);
			}
			// Delay resetting to prevent accidental clicks
			dragTimeoutRef.current = setTimeout(() => {
				setIsDragging(false);
				dragTimeoutRef.current = null;
			}, 100);
		});

		const clickListener = map.addListener('click', () => {
			onMapClick?.();
		});

		return () => {
			// Clear any pending drag timeout
			if (dragTimeoutRef.current) {
				clearTimeout(dragTimeoutRef.current);
				dragTimeoutRef.current = null;
			}
			if (window.google && window.google.maps) {
				[dragStartListener, dragEndListener, clickListener].forEach(
					(listener) => {
						window.google.maps.event.removeListener(listener);
					}
				);
			}
		};
	}, [map, onMapClick]);

	return isDragging;
};

export default useMapDragState;
